import React, { useRef, useEffect, useState } from "react";
import CommonPage from "@icarius-common/commonPage";
import EditableGridv2 from "@icarius-common/editableGrid/editableGridv2";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import AddRowStatusBar from '@icarius-common/editableGrid/components/addRow/addRowButton';
import paths from "@icarius-router/paths";
import { getLocalizedString } from "@icarius-localization/strings";
import { getConceptStructureFromServer, setConceptStructureToServer, deleteConceptStructureInServer } from "../actions";
import {
  getRows,
  getLoading,
  getCodeRefTypes,
  getColumnTypes,
  getCalculationIndicatorTypes,
  getInformedValueTypeTypes,
  getConcepts,
  getEntryModeTypes
} from "../selectors";
import { useDispatch, useSelector } from "react-redux";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import ConfirmationDialog from "./confirmationDialog";
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from "devextreme/excel_exporter"
import UploadFileDialog from "./uploadFileDialog";
import { openDialogAction } from "@icarius-common/dialog/actions";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField, Grid, } from "@material-ui/core";

const dummyEntity = {
  "type": "CON",
  "periodicity": "F",
  "hoursMinutes": "",
  "quantity": "",
  "percentage": "",
  "calculationBase": "",
  "reliquidation": "",
  "honoraries": "",
  "thirdPartyPayment": "",
  "imputationPeriod": "",
  "benefitCenter": "",
  "project": "",
  "codeRef": [],
  "advancePayment": "",
  "calculationIndicator": [],
  "entryMode": [],
  "concepts": [],
  "informedValue": "",
  "informedValueType": ""
};

const ConceptStructure = ({ history }) => {

  const [dataChanged, setDataChanged] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState(false);
  const [shouldReloadGrid, setShouldReloadGrid] = useState(false);
  const [uploadDialogIsOpen, setUploadDialogIsOpen] = useState(false);
  const [gridReference, setGridReference] = useState(null);

  const dispatch = useDispatch();
  const codeRefTypes = useSelector(getCodeRefTypes);
  const columnTypes = useSelector(getColumnTypes);
  const calculationIndicatorTypes = useSelector(getCalculationIndicatorTypes);
  const informedValueTypeTypes = useSelector(getInformedValueTypeTypes);
  const entryModeTypes = useSelector(getEntryModeTypes);
  const concepts = useSelector(getConcepts);

  const rowData = useSelector(getRows);
  const isLoading = useSelector(getLoading);

  const gridRef = useRef();

  useEffect(() => {
    dispatch(getConceptStructureFromServer());
    window.scrollTo(0, 0);
    setSelectedConcept(null);
  }, [dispatch])

  useEffect(() => {
    if (gridRef) {
      setGridReference(gridRef.current);
    }
  }, [gridRef])

  const handleSave = () => {
    const rowData = gridReference.getVisibleRows().map(item => item.data);
    const formattedData = rowData.map(row => {
      //Completo los campos faltantes
      row = { ...dummyEntity, ...row };

      const filtered = Object.keys(row)
        .filter(key => !key.includes("String"))
        .reduce((obj, key) => {
          obj[key] = row[key];
          return obj;
        }, {});

      filtered.calculationIndicator = filtered.calculationIndicator.filter(item => item).map(item => item.key ? item.key : item);
      filtered.codeRef = filtered.codeRef.filter(item => item).map(item => item.key ? item.key : item)
      filtered.entryMode = filtered.entryMode.filter(item => item).map(item => item.key ? item.key : item)
      filtered.concepts = filtered.concepts.filter(item => item).map(item => item.key ? item.key : item)

      return filtered;
    })

    dispatch(setConceptStructureToServer(formattedData))
      .then((resp) => {
        //limpiar el seleccionado
        gridReference.deselectAll()

        if (resp?.response?.data?.status === 'DUPLICATED_DATA') {
          const rowsToSelect = [];
          resp.response.data.fields.forEach(field => {
            const keysToSelect = resp.response.data[`${field}s`];
            gridReference.getVisibleRows().forEach(node => {
              if (keysToSelect.includes(node.data[field])) {
                rowsToSelect.push(node.key);
              }
            });

            gridReference.selectRows(rowsToSelect);
          })
        } else {
          setShouldReloadGrid(true);
        }

        if (resp?.status === 200) {
          setDataChanged(false);
        }
      })
  };


  const handleOpenDeleteDialog = () => {
    let selectedRows = gridReference.getSelectedRowsData();

    if (selectedRows.length < 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRow"), severity: "warning" }));
      return;
    }

    dispatch(openDialogAction({
      title: getLocalizedString("atention"),
      msg: getLocalizedString("doYouWantToDeleteSelectedRows"),
      onConfirm: () => deleteRowsFromServer(selectedRows),
    }));
  }

  const deleteRowsLocally = (rowsToDelete) => {
    gridReference.cancelEditData();

    if (Array.isArray(rowsToDelete)) {
      const ds = gridReference.getDataSource();
      const filter = gridReference.getCombinedFilter();

      ds.store().load({ sort: ds.sort(), filter: filter ? filter : null })
        .done((allData) => {
          const rowData = allData;

          rowsToDelete.forEach((row) => {
            const filteredRows = rowData.filter((el) => JSON.stringify(el) === JSON.stringify(row));
            filteredRows.forEach(gridRow =>
              gridReference.getDataSource().store().remove(gridRow)
                .then(() => {
                  gridReference.refresh();
                })
            )
          });
        });
    }

    dispatch(openSnackbarAction({ msg: getLocalizedString("structuresDeletedSuccessfully"), severity: "success" }));
  }

  const deleteRowsFromServer = (rowsToDelete) => {
    const codesToSend = rowsToDelete.filter(row => !row.hasOwnProperty("__KEY__")).map(row => row.code);

    if (codesToSend.length > 0) {
      dispatch(deleteConceptStructureInServer(codesToSend))
        .then((resp) => {
          //limpiar el seleccionado
          gridReference.deselectAll();
          if (['IN_USE_MODULES', 'IN_USE', 'IN_USE_ASSETSDISCOUNTS'].includes(resp?.response?.data?.status)) {
            const keysToSelect = resp.response.data.dependencies;
            const rowsToSelect = [];
            gridReference.getVisibleRows().forEach(node => {
              if (keysToSelect.includes(node.data.code)) {
                rowsToSelect.push(node.key);
              }
            });

            gridReference.selectRows(rowsToSelect);
          } else {
            deleteRowsLocally(rowsToDelete);
          }
        })
    } else if (rowsToDelete.length > codesToSend.length) {
      deleteRowsLocally(rowsToDelete);
    }
  }

  const handleImportStructures = () => {
    setUploadDialogIsOpen(true);
  };

  const handleGoBack = () => {
    history.push(paths.conceptsAndFormulasScreenSelector);
  }

  const handleCheckGoBack = () => {
    if (dataChanged) {
      setConfirmationDialogIsOpen(true);
    } else {
      handleGoBack();
    }
  }

  const handleExportGrid = () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(getLocalizedString("conceptInputStructure"));

    exportDataGrid({
      component: gridReference,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.value) {
          if (gridCell.rowType === 'data') {
            if (gridCell.column.dataField === 'num' && gridCell.value !== "") {
              excelCell.value = parseFloat(gridCell.value);
              excelCell.numFmt = '_(#,##0.000_);_((#,##0.000)';
            }
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Estructura de ingreso de conceptos.xlsx');
      });
    });
  }

  const saveItem = () => (
    <CustomIconButton
      title={getLocalizedString("save")}
      onClick={handleSave}
      type={"save"}
    />
  )

  const importItem = () => (
    <CustomIconButton
      title={getLocalizedString("importStructures")}
      onClick={handleImportStructures}
      type={"upload"}
    />
  )

  const deleteItem = () => (
    <CustomIconButton
      title={getLocalizedString("delete")}
      onClick={handleOpenDeleteDialog}
      type={"delete"}
    />
  )

  const addRowButton = () => (
    <AddRowStatusBar dataGridRef={gridRef} />
  )

  const goBackButton = () => (
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handleCheckGoBack}
      type={"goBack"}
    />
  )

  const exportGrid = () => (
    <CustomIconButton
      title={getLocalizedString("export")}
      onClick={handleExportGrid}
      type={"excel"}
    />
  )

  const selectDataSources = (name) => {
    switch (name) {
      case "periodicity":
        return columnTypes.filter((item) => ["P", "T", "F", "M", "C", "CP"].includes(item.key));
      case "hoursMinutes":
      case "quantity":
      case "percentage":
      case "calculationBase":
      case "reliquidation":
      case "imputationPeriod":
      case "benefitCenter":
      case "benefitCenter2":
      case "benefitCenter3":
      case "benefitCenter4":
      case "benefitCenter5":
      case "project":
      case "informedValue":
        return columnTypes.filter((item) => ["O", "B"].includes(item.key));
      case "honoraries":
      case "advancePayment":
      case "thirdPartyPayment":
        return columnTypes.filter((item) => item.key === "B");
      case "codeRef":
        return codeRefTypes;
      case "genericRef1":
        return columnTypes.filter((item) => ["O", "B"].includes(item.key));
      case "genericRef2":
        return columnTypes.filter((item) => ["O", "B"].includes(item.key));
      case "calculationIndicator":
        return calculationIndicatorTypes;
      case "informedValueType":
        return informedValueTypeTypes;
      case "entryMode":
        return entryModeTypes;
      case "concepts":
        return concepts;
      default:
        return [];
    }
  }

  const formattedData = rowData.map((row) => {
    return {
      ...row,
      codeRef: row.codeRef.length ? row.codeRef : [],
      calculationIndicator: row.calculationIndicator.length ? row.calculationIndicator : [],
      entryMode: row.entryMode.length ? row.entryMode : [],
      concepts: row.concepts.length ? row.concepts : [],
    }
  })

  useEffect(() => {
    rowData && setShouldReloadGrid(true);
  }, [rowData])

  const customHeader = () => (
    <Grid container item alignItems="center" xs={12} style={{ marginLeft: 20 }}>
      <Grid container item alignItems="center" xs={12}>
        <Grid container item alignItems="center" xs={3}>
          <Autocomplete
            disabled={concepts.length === 0}
            style={{ width: '100%' }}
            options={concepts}
            value={selectedConcept}
            onChange={(event, selectedConcept) => {
              setSelectedConcept(selectedConcept);

              let rowsToSelect = [];

              gridReference.getVisibleRows().forEach(node => {
                if (node.data.concepts.find(el => el.key === selectedConcept?.key)) {
                  rowsToSelect.push(node.key);
                }
              });

              gridReference.selectRows(rowsToSelect);
              if (rowsToSelect.length > 0) {
                gridReference.navigateToRow(rowsToSelect[0]).then(() => {
                  const rowElement = gridReference.getRowElement(gridReference.getRowIndexByKey(rowsToSelect[0]));
                  if (rowElement) {
                    rowElement[0].scrollIntoView({
                      behavior: "smooth",
                      block: "center"
                    });
                  }
                });
              }
            }}
            clearOnBlur
            clearText={getLocalizedString("clear")}
            noOptionsText={''}
            getOptionLabel={(data) => data?.value || data}
            renderInput={(params) => <TextField {...params} label={getLocalizedString("concept")} />}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <CommonPage
      title={getLocalizedString("conceptStructure")}
      menuItems={[goBackButton, addRowButton, saveItem, exportGrid, importItem, deleteItem]}
      isLoading={isLoading}
      customHeader={customHeader}
      isNotGridPage
    >
      <EditableGridv2
        selectDataSources={selectDataSources}
        setDataChanged={setDataChanged}
        ref={gridRef}
        gridName="conceptStructure"
        rowData={formattedData}
        setShouldReloadGrid={setShouldReloadGrid}
        shouldReloadGrid={shouldReloadGrid}
        columns={getColumnDefByPage(paths.conceptStructure)}
      />
      {
        confirmationDialogIsOpen &&
        <ConfirmationDialog
          open={confirmationDialogIsOpen}
          handleConfirm={handleGoBack}
          handleClose={() => setConfirmationDialogIsOpen(false)}
        />
      }
      {uploadDialogIsOpen &&
        <UploadFileDialog
          open={uploadDialogIsOpen}
          isLoading={isLoading}
          handleClose={() => setUploadDialogIsOpen(false)}
        />
      }
    </CommonPage>
  );
}

export default ConceptStructure;